import {
    DEFAULT_DROPOFF_CARRIER_KEYS,
    normalizeDropoffCarrierCatalog,
    normalizeDropoffCarrierKeys,
} from './dropoffCarriers';

export const MOBILE_MODES = {
    ORDER: 'order',
    TRIP: 'trip',
};

const MOBILE_MODES_VALUES = Object.values(MOBILE_MODES);

const normalizeMobileMode = (value) =>
    MOBILE_MODES_VALUES.includes(value) ? value : MOBILE_MODES.ORDER;

const normalizeMobileSettings = (ms = {}) => ({
    picking_mode: normalizeMobileMode(ms?.picking_mode),
    preparation_mode: normalizeMobileMode(ms?.preparation_mode),
});

const BOOLEAN_SETTINGS_DEFAULTS = {
    plan_require_vehicle: true,
    plan_require_driver: true,
    plan_require_transportation_company: true,
    plan_require_dropoff_carrier: true,
    use_custom_mailer: false,
    mailer_smtp_enable_starttls_auto: true,
    survey_rating_enabled: false,
    // Lo que la app le exige al chofer para cerrar una entrega. Los defaults son lo que
    // la app pedía hardcodeado desde Appery, para que activar esto no le cambie la
    // operación a ninguna empresa hasta que alguien entre a configurarlo.
    pod_require_signature: true,
    pod_require_receiver: true,
};

// El texto con el que la app móvil viene preguntando la evaluación desde Appery. Está
// duplicado en el backend (`Company::SURVEY_RATING_DEFAULT_QUESTION`), que es el que
// manda: acá sirve para que el campo muestre algo mientras no llegó la respuesta y para
// no mandar el texto vacío si el admin lo borró. Si cambia, cambian los dos.
export const SURVEY_RATING_DEFAULT_QUESTION = '¿Cómo evaluaría nuestro servicio?';

const normalizeSurveyRatingQuestion = (value) => {
    const text = typeof value === 'string' ? value.trim() : '';
    return text || SURVEY_RATING_DEFAULT_QUESTION;
};

const DEFAULT_RELBASE_DOC = { enabled: false, mode: null };

const normalizeRelbaseDoc = (doc = {}) => ({
    enabled: normalizeBoolean(doc?.enabled, false),
    mode: doc?.mode || null,
});

const normalizeBillingSettings = (bs = {}) => {
    const relbase = bs?.relbase || {};
    return {
        relbase: {
            sale_notes: normalizeRelbaseDoc(relbase.sale_notes),
            invoices: normalizeRelbaseDoc(relbase.invoices),
            quotations: normalizeRelbaseDoc(relbase.quotations),
        },
    };
};

const normalizeBoolean = (value, defaultValue) => {
    if (value === null || value === undefined) {
        return defaultValue;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (['true', '1', 'yes', 'si', 'on'].includes(normalizedValue)) {
            return true;
        }

        if (['false', '0', 'no', 'off', ''].includes(normalizedValue)) {
            return false;
        }
    }

    return Boolean(value);
};

const normalizeInteger = (value, defaultValue) => {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : defaultValue;
};

// Cuántas fotos exige la app para cerrar una entrega. El tope son las ranuras que dibuja
// la pantalla de firma (`Company::POD_MAX_PHOTOS` en el backend, que es quien manda);
// acá se recorta para no ofrecer un número que el backend va a rechazar.
export const POD_MAX_PHOTOS = 3;
export const POD_PHOTO_OPTIONS = [0, 1, 2, 3];

const normalizePodPhotos = (value) => {
    const parsed = normalizeInteger(value, 2);
    if (!Number.isInteger(parsed)) return 2;

    return Math.min(Math.max(parsed, 0), POD_MAX_PHOTOS);
};

// Fotos de los bultos al terminar de pickear en la app. Los valores son los de
// `Company::PICKING_PACKAGE_PHOTOS_MODES` en el backend; si cambian, cambian los dos.
export const PICKING_PACKAGE_PHOTOS_OPTIONS = [
    { value: 'off', label: 'No pedir fotos', help: 'La app no ofrece sacar fotos de los bultos.' },
    { value: 'optional', label: 'Opcional', help: 'Al terminar de pickear la app ofrece sacar fotos de los bultos, pero se puede saltar.' },
    { value: 'required', label: 'Obligatoria', help: 'No se puede cerrar el pickeo sin sacar al menos una foto de los bultos.' },
];

const normalizePickingPackagePhotos = (value) =>
    PICKING_PACKAGE_PHOTOS_OPTIONS.some((option) => option.value === value) ? value : 'off';

// Cuantos dias de viajes ve el chofer en la app (1 = solo hoy). El tope es la semana que la
// app mostraba siempre (`Company::DRIVER_TRIPS_WINDOW_MAX_DAYS` en el backend).
// Avisos que le llegan al cliente final. Las claves son las de
// `Company::CUSTOMER_EMAIL_KINDS` en el backend; si cambian, cambian las dos. Los
// que no tienen `subjectField` tienen el asunto fijo en el mailer (`fixedSubject`).
export const CUSTOMER_EMAIL_TYPES = [
    {
        key: 'scheduled',
        label: 'Pedido programado',
        subjectField: 'notif_scheduled_subject',
        help: 'Avisa al cliente que su pedido quedó programado para despacho y que se está preparando.',
    },
    {
        key: 'shipped',
        label: 'Pedido en camino',
        subjectField: 'notif_shipped_subject',
        help: 'Se envía cuando el chofer inicia la entrega. Incluye la fecha y el horario estimado de llegada.',
    },
    {
        key: 'delivered',
        label: 'Pedido entregado',
        subjectField: 'notif_delivered_subject',
        help: 'Se envía cuando el chofer cierra la entrega. Incluye quién recibió, la hora, el punto de recepción y las fotos.',
    },
    {
        key: 'ready_for_pickup',
        label: 'Listo para retiro',
        fixedSubject: 'Pedido listo para retiro',
        help: 'Para pedidos de retiro en tienda: avisa que el pedido ya está preparado y dónde retirarlo.',
    },
    {
        key: 'cancelled_by_driver',
        label: 'Cancelado por el chofer',
        fixedSubject: 'Pedido cancelado por el conductor',
        help: 'Se envía cuando el chofer no pudo concretar la entrega y la cancela. Le dice al cliente que se va a reagendar.',
    },
];

// No es un aviso más: apagado, ninguno de los de arriba sale para una entrega a un punto de
// dropoff (`Company::CUSTOMER_EMAIL_SETTINGS` en el backend).
export const CUSTOMER_EMAIL_DROPOFF = {
    key: 'dropoff',
    label: 'Pedidos con dropoff',
    help: 'Si eliges No, a los pedidos que van a un punto de dropoff (Starken, Blue Express, etc.) no les llega ninguno de los correos de arriba.',
};

// Una clave que no viene cuenta como encendida, igual que en el backend.
const normalizeCustomerEmailSettings = (ces = {}) =>
    Object.fromEntries([...CUSTOMER_EMAIL_TYPES, CUSTOMER_EMAIL_DROPOFF].map(({ key }) => [key, normalizeBoolean(ces?.[key], true)]));

export const DRIVER_TRIPS_WINDOW_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

const normalizeDriverTripsWindow = (value) => {
    const parsed = normalizeInteger(value, 7);
    if (!Number.isInteger(parsed)) return 7;

    return Math.min(Math.max(parsed, 1), 7);
};

export const normalizeCompanySettings = (data = {}) => {
    const normalizedData = { ...data };

    normalizedData.superadmin_only_document_types = Array.isArray(data?.superadmin_only_document_types)
        ? data.superadmin_only_document_types
        : [];

    Object.entries(BOOLEAN_SETTINGS_DEFAULTS).forEach(([field, defaultValue]) => {
        normalizedData[field] = normalizeBoolean(data?.[field], defaultValue);
    });

    normalizedData.manual_picking_limit_units = normalizeInteger(data?.manual_picking_limit_units, 30);
    normalizedData.pod_required_photos = normalizePodPhotos(data?.pod_required_photos);
    normalizedData.picking_package_photos = normalizePickingPackagePhotos(data?.picking_package_photos);
    normalizedData.driver_trips_window_days = normalizeDriverTripsWindow(data?.driver_trips_window_days);
    normalizedData.billing_settings = normalizeBillingSettings(data?.billing_settings);
    normalizedData.customer_email_settings = normalizeCustomerEmailSettings(data?.customer_email_settings);
    normalizedData.mobile_settings = normalizeMobileSettings(data?.mobile_settings);
    normalizedData.survey_rating_question = normalizeSurveyRatingQuestion(data?.survey_rating_question);

    const normalizedDropoffCarrierCatalog = normalizeDropoffCarrierCatalog(data?.available_dropoff_carriers);
    normalizedData.available_dropoff_carriers = normalizedDropoffCarrierCatalog;
    normalizedData.dropoff_carriers = normalizeDropoffCarrierKeys(
        data?.dropoff_carriers,
        normalizedDropoffCarrierCatalog,
        DEFAULT_DROPOFF_CARRIER_KEYS
    );

    return normalizedData;
};

export const serializeCompanySettingValue = (value) => {
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }

    if (value instanceof File || value instanceof Blob) {
        return value;
    }

    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
    }

    return value;
};
